import { useMemo, useSyncExternalStore } from 'react';

import { type Toast, type ToastInput } from '@/shared/utils/toast-store/toast-store.types';
import { toastStore } from '@/shared/utils/toast-store/toast-store.util';

export interface ToastPromiseCopy {
  loading: string;
  /** Called with the resolved value, so the message can name what was saved. */
  success: string | ((value: never) => string);
  /** Called with the rejection, so a server message can be surfaced verbatim. */
  error: string | ((reason: unknown) => string);
}

export interface UseToast {
  toasts: Toast[];
  toast: (input: ToastInput) => string;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
  /** Shows a pending toast and resolves it IN PLACE. Rethrows so callers still see failures. */
  promise: <T>(work: Promise<T>, copy: ToastPromiseCopy) => Promise<T>;
  dismiss: (id: string) => void;
}

function resolveCopy<T>(copy: string | ((value: T) => string), value: T): string {
  return typeof copy === 'string' ? copy : copy(value);
}

/**
 * Toasts, read through `useSyncExternalStore` (W9).
 *
 * All copy is CALLER-translated: the kit owns no domain string. `promise` shows
 * one pending toast and rewrites it in place rather than stacking a second, so a
 * slow mutation reads as one event and not two.
 *
 * It **rethrows** after toasting the failure. A helper that swallows the
 * rejection is how an optimistic table ends up never reverting — the caller's
 * `catch` has to run.
 */
export function useToast(): UseToast {
  const toasts = useSyncExternalStore(toastStore.subscribe, toastStore.getSnapshot);

  return useMemo(() => {
    const push = (input: ToastInput) => toastStore.push(input);
    const shorthand =
      (tone: ToastInput['tone']) =>
      (title: string, description?: string): string =>
        push({ tone, title, description });

    return {
      toasts,
      toast: push,
      success: shorthand('success'),
      error: shorthand('error'),
      warning: shorthand('warning'),
      info: shorthand('info'),
      dismiss: (id: string) => {
        toastStore.dismiss(id);
      },
      promise: async <T>(work: Promise<T>, copy: ToastPromiseCopy): Promise<T> => {
        const id = push({ tone: 'loading', title: copy.loading });

        try {
          const value = await work;
          toastStore.update(id, {
            tone: 'success',
            title: resolveCopy(copy.success as string | ((v: T) => string), value),
          });
          return value;
        } catch (reason) {
          toastStore.update(id, { tone: 'error', title: resolveCopy(copy.error, reason) });
          throw reason;
        }
      },
    };
  }, [toasts]);
}
